import { curry, curryN, pipe, converge, find, nthArg, propEq } from "ramda";

let tc = (window.tc = {});

export default tc;

tc.util = {};
tc.dateTime = {};
tc.dom = {};
tc.regExp = {};
tc.dataStructure = {};

/* RegExp helpers */

// String -> RegExp
tc.regExp.word = function (x) {
  return RegExp("\\b" + x + "\\b", "g");
};

/* Util helpers */

// String -> any -> any
tc.util.log = curry(function (tag, data) {
  console.log(tag, data);
  return data;
});

// tc.util.findById Any id -> [] -> {}
tc.util.findById = curryN(
  2,
  converge(find, [pipe(nthArg(0), propEq("id")), nthArg(1)])
);

// Function opposite to jQuery.param
tc.util.deparam = function (query) {
  query = query || "";
  var vars = query.split("&") || [query];
  return vars.filter(Boolean).reduce(function (acc, cur) {
    var pair = cur.split("=");
    pair[0] = decodeURIComponent(pair[0]);
    pair[1] = decodeURIComponent(pair[1]);
    // If first entry with this name
    if (typeof acc[pair[0]] === "undefined") {
      acc[pair[0]] = pair[1];
      // If second entry with this name
    } else if (typeof acc[pair[0]] === "string") {
      var arr = [acc[pair[0]], pair[1]];
      acc[pair[0]] = arr;
      // If third or later entry with this name
    } else {
      acc[pair[0]].push(pair[1]);
    }
    return acc;
  }, {});
};

tc.util.addLeadingZero = function (x) {
  return ("0" + x).slice(-2);
};

// Generate globally unique id
tc.util.guid = function () {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return (
    s4() +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    s4() +
    s4()
  );
};

/* Date utils */
// 2016-04-28 18:30:00 -> new Date("2016-04-28T18:30:00")
tc.dateTime.parse = function (x) {
  return new Date(x.replace(" ", "T"));
};

// 00:00 -> 0
tc.dateTime.timeToSeconds = function (str) {
  var parts = str.split(":"); // split it at the colons
  parts.forEach(function (part) {
    var number = parseInt(part, 10);
    if (isNaN(number)) {
      throw new TypeError("Time contains not a number");
    }
  });
  // minutes are worth 60 seconds. Hours are worth 60 minutes.
  var seconds = +parts[0] * 60 * 60 + +parts[1] * 60 + (+parts[2] || 0);
  return seconds;
};

/* Pure DOM helpers */

tc.dom.query = curry(function (query, elem) {
  return elem.querySelector(query);
});

tc.dom.queryAll = curry(function (query, elem) {
  return elem.querySelectorAll(query);
});

/**
 * Return elements parent node.
 * @param {Element} elem
 * @returns {Element}
 */
tc.dom.parent = function (elem) {
  return elem.parentNode;
};

tc.dom.parents = function (elem) {
  var parents = [];
  while (elem) {
    parents.unshift(elem);
    elem = elem.parentNode;
  }
  return parents;
};

tc.dom.parentsWhile = curry(function (fn, elem) {
  var arr = [];
  while (elem && !fn(elem)) {
    arr.unshift(elem);
    elem = elem.parentNode;
  }
  return arr;
});

// NodeList -> Array[Node]
tc.dom.toArray = function (nodeList) {
  var arr = [];
  for (var i = nodeList.length; i--; arr.unshift(nodeList[i]));
  return arr;
};

tc.dom.forEach = curry(function (fn, nodeList) {
  for (var i = 0, len = nodeList.length; i < len; i++) {
    fn(arr[i]);
  }
});

tc.dom.tagEq = curry(function (tag, elem) {
  return elem.tagName === tag;
});

tc.dom.setAttr = curry(function (attr, val, elem) {
  elem.setAttribute(attr, val.trim());
  return elem;
});

tc.dom.getAttr = curry(function (attr, elem) {
  return elem.getAttribute(attr);
});

tc.dom.attrEq = curry(function (attr, val, elem) {
  return tc.dom.getAttr(attr, elem) === val;
});

tc.dom.setClass = tc.dom.setAttr("class");
tc.dom.getClass = tc.dom.getAttr("class");

tc.dom.hasClass = curry(function (name, elem) {
  return tc.regExp.word(name).test(tc.dom.getClass(elem));
});

tc.dom.addClass = curry(function (name, elem) {
  var classes = tc.dom.getClass(elem);
  return tc.dom.setClass(classes ? classes + " " + name : name, elem);
});

tc.dom.removeClass = curry(function (name, elem) {
  return tc.dom.setClass(
    (tc.dom.getClass(elem) || "").replace(tc.regExp.word(name), ""),
    elem
  );
});

tc.dom.on = curry(function (name, fn, elem) {
  var attach = elem.on ? elem.on.bind(elem) : elem.addEventListener;
  attach(name, fn);
  return function () {
    tc.dom.off(name, fn, elem);
  };
});

tc.dom.off = curry(function (name, fn, elem) {
  var detach = elem.off ? elem.off.bind(elem) : elem.removeEventListener;
  return detach(name, fn);
});

tc.dom.dispatch = curry(function (name, config, elem) {
  var e = new CustomEvent(name, config);
  elem.dispatchEvent(e);
});

tc.dom.getCookie = function (key) {
  var matches = document.cookie.match(
    new RegExp(
      "(?:^|; )" +
        key.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") +
        "=([^;]*)"
    )
  );
  return matches ? decodeURIComponent(matches[1]) : undefined;
};

tc.dom.setCookie = function (key, value, options) {
  options = options || {};
  options.path = options.path || "/";

  var expires = options.expires;

  if (typeof expires == "number" && expires) {
    var d = new Date();
    d.setTime(d.getTime() + expires * 1000);
    expires = options.expires = d;
  }
  if (expires && expires.toUTCString) {
    options.expires = expires.toUTCString();
  }

  value = encodeURIComponent(value);

  var updatedCookie = key + "=" + value;

  for (var propName in options) {
    updatedCookie += "; " + propName;
    var propValue = options[propName];
    if (propValue !== true) {
      updatedCookie += "=" + propValue;
    }
  }

  document.cookie = updatedCookie;
};

var richObject =
  (tc.dataStructure.hash =
  tc.dataStructure.Hash =
    function (obj) {
      function Result() {}

      function T() {
        return true;
      }

      function iterator(fn, pred, acc, obj, breakable) {
        var res = acc;
        var keys = Object.keys(obj);
        var keysLen = keys.length;
        while (keysLen--) {
          var key = keys[keysLen];
          var predResult = pred.call(obj, obj[key], key, obj);
          res = fn.call(obj, res, obj[key], key, obj, predResult);
          if (breakable && !predResult) {
            break;
          }
        }
        return res;
      }

      function reduce(fn, base, obj) {
        return iterator(fn, T, base, obj, false);
      }

      function empty() {
        return richObject({});
      }

      Object.defineProperties(Result.prototype, {
        empty: {
          value: empty,
        },
        reduce: {
          value: function (fn, base) {
            return reduce(fn, base, this);
          },
        },
        forEach: {
          value: function (fn) {
            reduce(
              function (acc, cur, key) {
                fn.call(this, this[key], key, this);
              },
              this.empty(),
              this
            );
          },
        },
        map: {
          value: function (fn) {
            return reduce(
              function (acc, cur, key) {
                acc[key] = fn.call(this, this[key], key, this);
                return acc;
              },
              this.empty(),
              this
            );
          },
        },
        filter: {
          value: function (fn) {
            return iterator(
              function (acc, cur, key, obj, valid) {
                if (valid === true) {
                  acc[key] = cur;
                }
                return acc;
              },
              fn,
              this.empty(),
              this,
              false
            );
          },
        },
        /*flatten: {
      value: function () {
        return reduce(function (acc, cur, key) {
          // { a: {a: 0}, b: 1 } -> { a: 0, b: 1 }
          // [[1], [2]] -> [1, 2]
          if (cur !== null && typeof cur === 'object') {
            //acc
          } else {
            acc[key] = cur;
          }
          return acc;
        }, this.empty(), this);
      }
    },*/
        // groupBy
        pluck: {
          value: function (prop) {
            return this.map(function (item) {
              return item[prop];
            });
          },
        },
        every: {
          value: function (fn) {
            return iterator(
              function (acc, cur, key, obj, valid) {
                return valid;
              },
              fn,
              true,
              this,
              true
            );
          },
        },
        some: {
          value: function (fn) {
            return iterator(
              function (acc, cur, key, obj, valid) {
                if (valid === true) {
                  acc = true;
                }
                return acc;
              },
              fn,
              false,
              this,
              false
            );
          },
        },
        equals: {
          value: function (obj) {
            var theseKeys = Object.keys(this);
            var thoseKeys = Object.keys(obj);
            var theseKeysLength = theseKeys.length;
            var thoseKeysLength = thoseKeys.length;
            var key;
            var equals = true;
            if (theseKeysLength === 0 && thoseKeysLength === 0) {
              return true;
            }
            if (theseKeysLength !== thoseKeysLength) {
              return false;
            }
            while (theseKeysLength--) {
              key = theseKeys[theseKeysLength];
              if (this[key] !== obj[key]) {
                equals = false;
                break;
              }
            }
            return equals;
          },
        },
        concat: {
          value: function (obj) {
            return richObject(Object.assign({}, this, obj));
          },
        },
        ap: {
          value: function (app) {
            return app.map(this);
          },
        },
        chain: {
          value: function (fn) {
            return fn(this);
          },
        },
        find: {
          value: function (fn) {
            return iterator(
              function (acc, cur, key, obj, valid) {
                if (valid === true) {
                  acc = cur;
                }
                return acc;
              },
              fn,
              undefined,
              this,
              true
            );
          },
        },
        length: {
          get: function () {
            return Object.keys(this).length;
          },
        },
      });

      return Object.assign(Object.create(Result.prototype), obj);
    });

tc.dataStructure.hash.of = tc.dataStructure.Hash.of = function (obj) {
  return tc.dataStructure.hash(obj);
};
